// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  useState,
  useEffect,
  type Dispatch,
  type SetStateAction,
  useMemo,
} from 'react';
import { calculateDataGraphic } from '../utils/calculate-data-graphic';
import { calculateProfits } from '@shared/ui-kit/utils/calculate-profits';
import { PerformanceChart } from './performance-chart/performance-chart';
import { useAppSelector } from '@advisor-app/store/store';
import { WrapperEffect } from '@advisor-app/features/ui/wrapper-effect';
import { capitalizeString } from '@shared/ui-kit/utils/capitalize-string';
import {
  selectBroker,
  selectAccountType,
  selectAccount,
} from '@advisor-app/store/slices/broker';
import { portfolioApi } from '@advisor-app/store/api/brokerage/portfolio-api';
import { useTranslation } from 'react-i18next';

interface HistoryPortfolioProps {
  equity: number | undefined;

  profitsAndPercentage: {
    profit: number;
    percentage: number;
  };

  setProfitsAndPercentage: Dispatch<
    SetStateAction<{
      profit: number;
      percentage: number;
    }>
  >;
}

export const HistoryPortfolio = ({
  setProfitsAndPercentage,
}: HistoryPortfolioProps) => {
  const { t } = useTranslation();
  const broker = useAppSelector(selectBroker);
  const accountType = useAppSelector(selectAccountType);
  const account = useAppSelector(selectAccount);
  const initialPeriod = useMemo(
    () => (broker === 'interactive_brokers' ? '1W' : '1D'),
    [broker],
  );
  const [period, setPeriod] = useState(initialPeriod);
  const [dataHistory, setDataHistory] = useState<number[][]>([[]]);
  const today = new Date();
  const firstDayOfYear = new Date(today.getFullYear(), 0, 1);
  const pastDays = Math.ceil(
    (today.valueOf() - firstDayOfYear.valueOf()) / 86400000,
  );

  const buttons = [
    { key: t('glossary.period-1D', { ns: 'glossary' }), value: '1D' },
    { key: t('glossary.period-1W', { ns: 'glossary' }), value: '1W' },
    { key: t('glossary.period-1M', { ns: 'glossary' }), value: '1M' },
    { key: t('glossary.period-3M', { ns: 'glossary' }), value: '3M' },
    { key: t('glossary.period-1Y', { ns: 'glossary' }), value: '1A' },
    { key: t('glossary.period-YTD', { ns: 'glossary' }), value: `${pastDays}D` },
  ];

  const displayButtons = buttons.filter(({ value }) => {
    if (broker === 'interactive_brokers') {
      return value !== '1D';
    }
    return true;
  });

  const {
    data: performance,
    isFetching,
    isError,
  } = portfolioApi.usePerformanceQuery({
    params: {
      period: period,
      broker_key: broker.toLowerCase(),
      type_account: accountType,
    },
    payload: {
      account_id: account.accountId,
    },
  });

  useEffect(() => {
    if (performance) {
      setDataHistory(
        calculateDataGraphic(
          performance?.outstanding_balance as number[],
          performance?.datetime as string[],
        ),
      );
      // istanbul ignore next
      calculateProfits(performance.outstanding_balance, vals =>
        setProfitsAndPercentage({
          profit: vals[0],
          percentage: vals[1],
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [performance]);

  const updateData = (timeline: string) => {
    setPeriod(timeline);
  };

  return (
    <div className="box100-container px-2">
      <div className="box100-head-broker">
        {!isError ? (
          <div className="box100-toolbar-history flex">
            {displayButtons.map(({ key, value }) => (
              <button
                key={key}
                onClick={() => updateData(value)}
                className={
                  period === value
                    ? 'btn-filter-history active-graphic-period'
                    : 'btn-filter-history'
                }
              >
                {key}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <WrapperEffect showIn={!isFetching}>
        {dataHistory?.[0]?.length > 0 ? (
          <div className="h-80">
            <PerformanceChart
              data={dataHistory}
              period={period}
              loading={isFetching}
            />
          </div>
        ) : (
          <span className="block text-center text-lg font-semibold text-neutral-500">
            {`${t('dashboard.performance-graph-not-available', {
              ns: 'dashboard',
            })} ${capitalizeString(broker)} at the moment`}
          </span>
        )}
      </WrapperEffect>
    </div>
  );
};
